import { useEffect, useRef, useState } from 'react'
import { RiLockLine } from 'react-icons/ri'
import { Spinner } from '@/components/ui'
import { loadMercadoPagoSdk } from '@/lib/mercadopago'
import { logger } from '@/lib/logger'
import styles from './MercadoPagoForm.module.css'

interface MercadoPagoFormProps {
    /** Mercado Pago public key (APP_USR-… / TEST-…). */
    publicKey: string
    /** Amount to charge (decimal, e.g. 100.00 — NOT céntimos). */
    amount: number
    /** Customer email — pre-fills the Brick's payer field. */
    payerEmail: string
    /**
     * Called when the Brick has tokenized a card and is ready to submit. The
     * parent owns the network call to /api/payments/mercadopago/process/.
     * Returning a rejected promise keeps the Brick in its loading state and
     * surfaces the error to the user.
     */
    onSubmitPayment: (cardFormData: MercadoPagoCardFormData) => Promise<void>
    /** The Brick reported a tokenization / SDK error. */
    onError: (message: string) => void
}

const BRICK_CONTAINER_ID = 'mercadopago-card-brick-container'

/**
 * Mercado Pago Card Payment Brick component.
 *
 * Mounts the Brick into a fixed-id `<div>`, hands the tokenized form data to
 * the parent via `onSubmitPayment` (which calls the backend), and tears the
 * Brick down cleanly on unmount so a second mount cannot end up with two
 * Bricks fighting for the same DOM node.
 *
 * The Brick handles 3DS automatically when the issuing bank requires it; no
 * manual 3DS step is needed on this side.
 */
export function MercadoPagoForm({
    publicKey,
    amount,
    payerEmail,
    onSubmitPayment,
    onError,
}: MercadoPagoFormProps) {
    const [ready, setReady] = useState(false)
    const [processing, setProcessing] = useState(false)
    const controllerRef = useRef<MercadoPagoBrickController | null>(null)

    // Keep the latest callbacks reachable inside the Brick's async `onSubmit`
    // without re-running the mount effect (re-creating the Brick on every
    // parent re-render would flicker the iframe).
    const onSubmitRef = useRef(onSubmitPayment)
    const onErrorRef = useRef(onError)
    onSubmitRef.current = onSubmitPayment
    onErrorRef.current = onError

    useEffect(() => {
        const mounted = { current: true }

        loadMercadoPagoSdk()
            .then(async () => {
                if (!mounted.current) return
                if (!window.MercadoPago) {
                    throw new Error('Mercado Pago no está disponible')
                }
                const mp = new window.MercadoPago(publicKey, { locale: 'es-PE' })
                const bricks = mp.bricks()

                const controller = await bricks.create('cardPayment', BRICK_CONTAINER_ID, {
                    initialization: {
                        amount,
                        payer: { email: payerEmail },
                    },
                    customization: {
                        visual: { style: { theme: 'default' } },
                        paymentMethods: { maxInstallments: 12 },
                    },
                    callbacks: {
                        onReady: () => {
                            if (mounted.current) setReady(true)
                        },
                        onSubmit: (cardFormData: MercadoPagoCardFormData) => {
                            if (mounted.current) setProcessing(true)
                            return onSubmitRef.current(cardFormData)
                                .finally(() => {
                                    if (mounted.current) setProcessing(false)
                                })
                        },
                        onError: (error: MercadoPagoBrickError) => {
                            // Never surface MP's raw error.message — it can leak
                            // internal/fraud detail. Log via the sanitizing logger
                            // and show the user a friendly generic message.
                            logger.error('Mercado Pago brick error', error)
                            if (mounted.current) {
                                setProcessing(false)
                                onErrorRef.current(
                                    'No pudimos procesar tu pago. Intenta de nuevo o escríbenos a info@qolca.org.',
                                )
                            }
                        },
                    },
                })

                controllerRef.current = controller
            })
            .catch((err) => {
                logger.error('Mercado Pago brick init failed', err)
                if (mounted.current) {
                    onErrorRef.current('No se pudo cargar el formulario de pago. Intenta de nuevo.')
                }
            })

        return () => {
            mounted.current = false
            const controller = controllerRef.current
            controllerRef.current = null
            if (controller) {
                try {
                    const maybePromise = controller.unmount()
                    if (maybePromise && typeof (maybePromise as Promise<void>).catch === 'function') {
                        (maybePromise as Promise<void>).catch(() => {
                            /* unmount can race with destroy — safe to ignore */
                        })
                    }
                } catch {
                    /* Brick may not be mounted — safe to ignore */
                }
            }
        }
    }, [publicKey, amount, payerEmail])

    return (
        <div className={styles.wrapper}>
            {!ready && (
                <div className={styles.loading}>
                    <Spinner size="md" />
                    <p className={styles.loadingText}>Preparando el pago seguro...</p>
                </div>
            )}

            <div
                id={BRICK_CONTAINER_ID}
                className={styles.brickContainer}
                style={{ display: ready ? 'block' : 'none' }}
            />

            {ready && (
                <p className={styles.securityNote}>
                    <RiLockLine className={styles.lockIcon} />
                    Pago seguro procesado por Mercado Pago
                </p>
            )}

            {processing && (
                <div className={styles.processingOverlay}>
                    <div className={styles.processingCard}>
                        <Spinner size="lg" />
                        <p className={styles.processingTitle}>Procesando tu pago...</p>
                        <p className={styles.processingText}>
                            No cierres esta ventana. Estamos verificando tu transacción.
                        </p>
                    </div>
                </div>
            )}
        </div>
    )
}
